import React from 'react';
import { GlassCard } from '@/design-system';
import { DollarSign, TrendingUp, Building2 } from 'lucide-react';
import { formatCurrencyFull, formatTeamWeeks, formatPercent } from '@/utils/formatters';

interface InvestmentByIS {
  investibleStructureId: string;
  investibleStructureName: string;
  category: string;
  totalInvestment: number;
  teamWeeks: number;
  epicCount: number;
  percentage: number;
}

interface InvestmentByISChartProps {
  investmentData: InvestmentByIS[] | null;
  isLoading: boolean;
}

const InvestmentByISChart: React.FC<InvestmentByISChartProps> = ({
  investmentData,
  isLoading,
}) => {
  const getCategoryColor = (category: string) => {
    switch (category.toUpperCase()) {
      case 'BUSINESS':
        return 'bg-category-business';
      case 'TECH':
        return 'bg-category-tech';
      case 'SUPPORT':
        return 'bg-category-support';
      default:
        return 'bg-muted';
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category.toUpperCase()) {
      case 'BUSINESS':
        return 'text-category-business-text bg-category-business-bg';
      case 'TECH':
        return 'text-category-tech-text bg-category-tech-bg';
      case 'SUPPORT':
        return 'text-category-support-text bg-category-support-bg';
      default:
        return 'text-muted-foreground bg-surface-muted';
    }
  };

  if (isLoading) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <div className="animate-pulse">
          <div className="h-6 bg-muted/50 dark:bg-muted/30 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="h-4 bg-muted/50 dark:bg-muted/30 rounded w-1/3"></div>
                <div className="h-2 bg-muted/50 dark:bg-muted/30 rounded flex-1"></div>
                <div className="h-4 bg-muted/50 dark:bg-muted/30 rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!investmentData || investmentData.length === 0) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <h3 className="text-lg font-semibold text-foreground mb-4">Investment by IS</h3>
        <div className="text-center text-muted-foreground py-8">
          No investment data available
        </div>
      </GlassCard>
    );
  }

  const totalInvestment = investmentData.reduce((sum, item) => sum + item.totalInvestment, 0);
  const totalTeamWeeks = investmentData.reduce((sum, item) => sum + item.teamWeeks, 0);

  return (
    <GlassCard className="p-6 rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-blue to-indigo-600 flex items-center justify-center shadow-lg">
            <Building2 className="w-5 h-5 text-text-inverse" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Investment by IS</h3>
            <p className="text-sm text-muted-foreground">
              Portfolio distribution across Investible Structures
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-foreground">
            {formatCurrencyFull(totalInvestment)}
          </div>
          <div className="text-sm text-muted-foreground">
            {formatTeamWeeks(totalTeamWeeks)} Total
          </div>
        </div>
      </div>

      {/* Investment Items */}
      <div className="space-y-4">
        {[...investmentData]
          .sort((a, b) => b.totalInvestment - a.totalInvestment)
          .map((item, index) => (
            <div key={item.investibleStructureId} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      #{index + 1}
                    </span>
                    <span className="font-medium text-foreground">
                      {item.investibleStructureName}
                    </span>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor(item.category)}`}>
                    {item.category}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-medium text-foreground">
                    {formatCurrencyFull(item.totalInvestment)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatPercent(item.percentage)} of total
                  </div>
                </div>
              </div>

              {/* Investment Bar */}
              <div className="relative">
                <div className="w-full bg-surface-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getCategoryColor(item.category)}`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>

              {/* Additional Metrics */}
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatTeamWeeks(item.teamWeeks, { full: true })}</span>
                <span>{item.epicCount} EPICs</span>
                <span>
                  {item.teamWeeks > 0 ? formatCurrencyFull(item.totalInvestment / item.teamWeeks) : '$0'} per TW
                </span>
              </div>
            </div>
          ))}
      </div>

      {/* Top Performers */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="font-medium text-foreground mb-4">Top Investment Areas</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {investmentData.slice(0, 3).map((item, index) => (
            <div key={item.investibleStructureId} className="text-center p-3 rounded-xl bg-gradient-to-br from-surface-muted to-surface-muted/50">
              <div className="flex items-center justify-center mb-2">
                {index === 0 && <TrendingUp className="w-5 h-5 text-status-warning" />}
                {index === 1 && <DollarSign className="w-5 h-5 text-muted-foreground" />}
                {index === 2 && <DollarSign className="w-5 h-5 text-chart-orange" />}
              </div>
              <div className="font-medium text-foreground text-sm">
                {item.investibleStructureName}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCurrencyFull(item.totalInvestment)}
              </div>
              <div className="text-xs text-muted">
                {formatPercent(item.percentage)} of portfolio
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Investment Distribution Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-3 rounded-xl bg-accent-blue-bg/50">
            <div className="text-lg font-bold text-foreground">
              {investmentData.length}
            </div>
            <div className="text-xs text-muted-foreground">Active IS</div>
          </div>
          <div className="p-3 rounded-xl bg-accent-blue-bg/50">
            <div className="text-lg font-bold text-foreground">
              {formatCurrencyFull(totalInvestment / investmentData.length)}
            </div>
            <div className="text-xs text-muted-foreground">Avg Investment</div>
          </div>
          <div className="p-3 rounded-xl bg-status-success-bg/50">
            <div className="text-lg font-bold text-foreground">
              {investmentData.reduce((sum, item) => sum + item.epicCount, 0)}
            </div>
            <div className="text-xs text-muted-foreground">Total EPICs</div>
          </div>
          <div className="p-3 rounded-xl bg-status-warning-bg/50">
            <div className="text-lg font-bold text-foreground">
              {formatTeamWeeks(totalTeamWeeks / investmentData.length).replace(' TW', '')}
            </div>
            <div className="text-xs text-muted-foreground">Avg TW per IS</div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

export default InvestmentByISChart;
